'use client';
import { useState } from 'react';
import type { StockFactors, PillarScores } from '@/lib/compute';
import { QUALITY_FACTORS } from '@/lib/clientScore';
import { FactorRow } from './FactorRow';

interface Props {
  enabledFactors: Set<keyof StockFactors>;
  onToggleFactor: (key: keyof StockFactors) => void;
  pillarWeights: Record<keyof PillarScores, number>;
  highlightedFactor: keyof StockFactors | null | undefined;
  onFactorHighlight?: (key: keyof StockFactors) => void;
  highlightedPillar: keyof PillarScores | null | undefined;
  onPillarHighlight?: (pillar: keyof PillarScores) => void;
  infoKey: string | null;
  setInfoKey: (k: string | null) => void;
  /** Canonical Q-factor count from data/model-stats.json. Displayed in the
   *  panel header so it matches the deck (40 factors today). */
  canonicalCount?: number;
}

export function QualityFactorsPanel({
  enabledFactors,
  onToggleFactor,
  pillarWeights,
  highlightedFactor,
  onFactorHighlight,
  highlightedPillar,
  onPillarHighlight,
  infoKey,
  setInfoKey,
  canonicalCount,
}: Props) {
  const [open, setOpen] = useState(false);
  const [expandedPillar, setExpandedPillar] = useState<string | null>(null);

  const togglePillar = (pillar: keyof typeof QUALITY_FACTORS) => {
    const factors = QUALITY_FACTORS[pillar];
    const allEnabled = factors.every(f => enabledFactors.has(f.key));
    for (const f of factors) {
      if (allEnabled) {
        if (enabledFactors.has(f.key)) onToggleFactor(f.key);
      } else {
        if (!enabledFactors.has(f.key)) onToggleFactor(f.key);
      }
    }
  };

  return (
    <div className="px-5 py-2 border-b border-[#1e293b]">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full cursor-pointer">
        <label className="text-[#e2e8f0] text-[10px] font-semibold uppercase tracking-widest cursor-pointer">
          Quality Factors
          {canonicalCount != null && (
            <span className="text-[#64748b] ml-2 normal-case tracking-normal font-normal">{canonicalCount}</span>
          )}
        </label>
        <svg className={`w-3 h-3 text-white transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="mt-3 space-y-1">
          {(Object.entries(QUALITY_FACTORS) as [keyof typeof QUALITY_FACTORS, typeof QUALITY_FACTORS[keyof typeof QUALITY_FACTORS]][]).map(([pillar, factors]) => {
            // Skip momentum — rendered in its own panel below
            if (pillar === 'momentum') return null;
            const allEnabled = factors.every(f => enabledFactors.has(f.key));
            const someEnabled = factors.some(f => enabledFactors.has(f.key));
            const isExpanded = expandedPillar === pillar;
            return (
              <div key={pillar}>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => togglePillar(pillar)} className="flex items-center cursor-pointer">
                    <span className={`w-2.5 h-2.5 rounded-sm border flex items-center justify-center text-[7px]
                                    ${allEnabled ? 'bg-blue-500 border-blue-500 text-white' :
                                      someEnabled ? 'bg-blue-500/30 border-blue-500/50 text-white' :
                                      'border-[#334155]'}`}>
                      {allEnabled && '✓'}
                      {someEnabled && !allEnabled && '—'}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setExpandedPillar(isExpanded ? null : pillar);
                      onPillarHighlight?.(pillar as keyof PillarScores);
                    }}
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    <svg className={`w-2.5 h-2.5 text-[#64748b] transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className={`text-[10px] font-semibold capitalize ${highlightedPillar === pillar ? 'text-amber-400' : 'text-[#e2e8f0]'}`}>
                      {pillar}{highlightedPillar === pillar ? ' ✨' : ''}
                    </span>
                    <span className="text-[9px] text-[#64748b]">({factors.length}) [{pillarWeights[pillar as keyof PillarScores]}%]</span>
                  </button>
                </div>
                {isExpanded && (
                  <div className="ml-4 mt-1 flex flex-col gap-0">
                    {factors.map(f => (
                      <FactorRow
                        key={f.key}
                        factorKey={f.key}
                        enabled={enabledFactors.has(f.key)}
                        onToggle={() => onToggleFactor(f.key)}
                        highlighted={highlightedFactor === f.key}
                        onHighlight={() => onFactorHighlight?.(f.key as keyof StockFactors)}
                        infoOpen={infoKey === f.key}
                        onToggleInfo={() => setInfoKey(infoKey === f.key ? null : f.key)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
